import React, { useState, useEffect, useCallback } from 'react';
import { Search, Edit, Trash2, Users, MapPin, Phone, AlertCircle, Crown, Shield, User, UserCheck, UserX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AdvancedPagination from '../components/AdvancedPagination';
import ConfirmDialog from '../components/ConfirmDialog';
import { FormSelect } from '../components/FormSelect';

const UserManagement = () => {
  const { isSystemAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search and Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [limitPerPage, setLimitPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // ConfirmDialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminAPI.getUsers();

      if (response.data.success) {
        const usersData = response.data.data.map(user => ({
          id: user.id,
          username: user.username,
          phone: user.phone,
          full_name: user.full_name,
          user_type: user.user_type || 'staff',
          is_active: user.is_active,
          is_deleted: user.is_deleted || 0,
          locations: user.locations || [],
          created_at: user.created_at,
          last_login: user.last_login
        }));
        setUsers(usersData);
        setTotalCount(usersData.length);
      }

    } catch (err) {
      console.error('Failed to load users:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, currentPage, limitPerPage]);

  const getRoleText = (userType) => {
    switch (userType) {
      case 'system_admin': return 'Sistem Yöneticisi';
      case 'patron': return 'Patron';
      case 'staff': return 'Personel';
      default: return userType;
    }
  };

  const getRoleIcon = (userType) => {
    switch (userType) {
      case 'system_admin': return Shield;
      case 'patron': return Crown;
      case 'staff': return User;
      default: return User;
    }
  };

  const getRoleColor = (userType) => {
    switch (userType) {
      case 'system_admin': return 'bg-purple-100 text-purple-800';
      case 'patron': return 'bg-yellow-100 text-yellow-800';
      case 'staff': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
        <UserCheck className="w-3 h-3 mr-1" />
        Aktif
      </span>
    ) : (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center">
        <UserX className="w-3 h-3 mr-1" />
        Pasif
      </span>
    );
  };


  // Handle delete click - opens confirmation dialog
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDialogOpen(true);
  };

  // Confirm deletion
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await adminAPI.deleteUser(userToDelete.id);
      setSuccess(`${userToDelete.full_name} başarıyla silindi.`);
      setDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(handleApiError(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter users - show only patrons in the table
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.user_type === roleFilter;
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && user.is_active) ||
                         (statusFilter === 'inactive' && !user.is_active);
    const isPatron = user.user_type === 'patron';
    return matchesSearch && matchesRole && matchesStatus && isPatron;
  });

  // Paginate filtered users
  const startIndex = (currentPage - 1) * limitPerPage;
  const endIndex = startIndex + limitPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUsers.length / limitPerPage);

  if (!isSystemAdmin()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <UserCheck className="h-5 w-5 text-green-400 mr-3" />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
        <div>
        </div>
        <div className="flex space-x-3">
          <Link to="/admin/users/new" className="btn-secondary flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Yeni Kullanıcı
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Ad, e-posta veya kullanıcı adı ile ara..."
                  className="form-input pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div>
              <FormSelect
                name="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'Tüm Durumlar' },
                  { value: 'active', label: 'Aktif' },
                  { value: 'inactive', label: 'Pasif' }
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-7 w-7 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Kullanıcı</p>
                <p className="text-3xl font-bold text-gray-900">{users.length}</p>
                <p className="text-xs text-gray-500">Sistem geneli</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Crown className="h-7 w-7 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Patron</p>
                <p className="text-3xl font-bold text-gray-900">
                  {users.filter(u => u.user_type === 'patron').length}
                </p>
                <p className="text-xs text-gray-500">İşletme sahipleri</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <User className="h-7 w-7 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Personel</p>
                <p className="text-3xl font-bold text-gray-900">
                  {users.filter(u => u.user_type === 'staff').length}
                </p>
                <p className="text-xs text-gray-500">Çalışanlar</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <UserCheck className="h-7 w-7 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aktif</p>
                <p className="text-3xl font-bold text-gray-900">
                  {users.filter(u => u.is_active).length}
                </p>
                <p className="text-xs text-gray-500">Aktif kullanıcılar</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Kullanıcı Listesi</h3>
            <span className="text-sm text-gray-500">
              {filteredUsers.length} kullanıcı bulundu
            </span>
          </div>
        </div>
        <div className="card-body">
          {paginatedUsers.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İletişim
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lokasyonlar
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kayıt Tarihi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedUsers.map((user) => {
                      const RoleIcon = getRoleIcon(user.user_type);
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${getRoleColor(user.user_type)}`}>
                                <RoleIcon className="h-5 w-5" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                                <div className="text-sm text-gray-500">@{user.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Phone className="h-4 w-4 text-gray-400 mr-2" />
                              {user.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getRoleColor(user.user_type)}`}>
                              {getRoleText(user.user_type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              {user.locations.slice(0, 2).map((location, index) => (
                                <div key={index} className="flex items-center text-sm text-gray-900">
                                  <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                  <span>{location.name}</span>
                                </div>
                              ))}
                              {user.locations.length > 2 && (
                                <span className="text-xs text-gray-500">
                                  +{user.locations.length - 2} daha
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(user.is_active)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/admin/users/edit/${user.id}`}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Düzenle"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                              {user.username !== 'admin' && (
                                <button
                                  onClick={() => handleDeleteClick(user)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Sil"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {paginatedUsers.map((user) => {
                  const RoleIcon = getRoleIcon(user.user_type);
                  return (
                    <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${getRoleColor(user.user_type)}`}>
                            <RoleIcon className="h-6 w-6" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                            <div className="text-sm text-gray-500">@{user.username}</div>
                            <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${getRoleColor(user.user_type)}`}>
                              {getRoleText(user.user_type)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(user.is_active)}
                        </div>
                      </div>

                      <div className="mt-4 space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 mr-2" />
                          {user.phone}
                        </div>
                        {user.locations.length > 0 && (
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-2" />
                            {user.locations.length} lokasyon
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          {new Date(user.created_at).toLocaleDateString('tr-TR')}
                        </span>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/admin/users/edit/${user.id}`}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          {user.username !== 'admin' && (
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              <AdvancedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={filteredUsers.length}
                limitPerPage={limitPerPage}
                onPageChange={setCurrentPage}
                onLimitChange={(newLimit) => {
                  setLimitPerPage(newLimit);
                  setCurrentPage(1);
                }}
              />
            </>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Kullanıcı bulunamadı</h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'Arama kriterlerinize uygun kullanıcı bulunmuyor.'
                  : 'Henüz hiç kullanıcı eklenmemiş.'}
              </p>
              <Link to="/admin/users/new" className="btn-primary">
                İlk Kullanıcıyı Ekle
              </Link>
            </div>
          )}
        </div>
      </div>


      {/* ConfirmDialog */}
      <ConfirmDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Kullanıcı Sil"
        message={`"${userToDelete?.full_name}" kullanıcısını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default UserManagement;